/**
 * Auth context and hook for the frontend.
 * Wraps Better Auth client session management.
 */
import { createContext, useContext, useEffect, useState, useCallback, useRef, type ReactNode } from 'react'
import { authClient } from '@papan/auth/client'
import { onApiFailure, getShareSessionToken } from '@papan/api-client'
import { sessionRotationInFlight } from '@papan/auth/session-rotation'
import { isSessionDeath401, isTenantDenied403 } from '../lib/session-loss'
import { announceSignedIn, announceSignedOut, onSignedInElsewhere, onSignedOutElsewhere } from '../lib/sign-out-broadcast'
import { endSession } from '@papan/core-ui/ui/sessionEnded'
import { accountEndReloadTarget, reloadAfterAccountEnd } from '../lib/reloadAfterAccountEnd'

interface AuthUser {
  id: string
  email: string
  name: string
  personalTenantId: string
  emailVerified: boolean
  /** 'user' | 'admin' — platform-level super admin. Returned by Better Auth as
   *  an additionalField (see shared/auth/src/server.ts). The SPA uses it only
   *  to decide whether to render platform-admin surfaces (Settings → Platform →
   *  Branding, and the Tier-1 budget/pricing pages); it grants nothing. Every
   *  /api/admin/* route re-checks it server-side via `requirePlatformAdmin`. */
  platformRole: string
}

function sameUser(a: AuthUser, b: AuthUser): boolean {
  return a.id === b.id && a.email === b.email && a.name === b.name && a.personalTenantId === b.personalTenantId
    && a.emailVerified === b.emailVerified && a.platformRole === b.platformRole
}

interface AuthContextValue {
  user: AuthUser | null
  /** The user's personal tenant ID (from their account). For active tenant, use useTenant(). */
  personalTenantId: string | null
  isLoading: boolean
  isAuthenticated: boolean
  /** True while a DELIBERATE logout is in progress / just happened (#501). The
   *  login guard reads it to redirect to a clean `/login` instead of
   *  `/login?next=…` — a user who chose to leave should not be bounced back to
   *  their previous page on the next sign-in. Involuntary unauth (session death,
   *  hitting a protected URL while logged out) leaves this false and keeps
   *  `?next=`. Reset once a session is re-established. */
  signingOut: boolean
  signOut: () => Promise<void>
  refreshSession: () => Promise<AuthUser | null>
}

const AuthContext = createContext<AuthContextValue | null>(null)

interface SessionProbe {
  data?: { user?: { id: string; email: string; name: string; personalTenantId?: string; tenantId?: string; emailVerified?: boolean; platformRole?: string | null } } | null
  error?: { status?: number } | null
  /** The call threw (network error, aborted) — no answer at all. */
  thrown?: true
}

/** #1316 — at most one tenant-denied re-check of the session per this window. */
const TENANT_DENIED_RECHECK_MS = 10_000
/** #1316 — a tab coming back into view re-checks who is signed in at most once per window. */
const RETURN_RECHECK_MS = 10_000
/** #1316 — an account re-check that meets a password change in flight asks again this often until it has landed. */
const ROTATION_RECHECK_MS = 1_000

let SESSION_RETRY_DELAYS_MS: readonly number[] = [500, 1000, 2000, 4000, 8000]
/** Tests only — shorten the backoff. */
export function setSessionRetryDelaysForTests(delays: readonly number[]): void { SESSION_RETRY_DELAYS_MS = delays }

async function probeSession(): Promise<SessionProbe> {
  try {
    return await authClient.getSession() as unknown as SessionProbe
  } catch {
    return { thrown: true }
  }
}

/** No answer about the session: it threw, or failed with anything but a 401 / 403 (both are answers). */
function isTransientProbe(p: SessionProbe): boolean {
  if (p.thrown) return true
  if (p.data?.user) return false
  return !!p.error && p.error.status !== 401 && p.error.status !== 403
}

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const userNow = useRef<AuthUser | null>(null)
  userNow.current = user
  /** #1316 — the user whose session a NAMED end (sign-out, signed out elsewhere) already ended: the user change that
   *  follows is that same end, never a second one (a second bumps the session epoch again and remounts the new tree). */
  const endedForUserId = useRef<string | null>(null)
  const sessionUserRef = useRef<string | null>(null)
  // #501 — distinguishes a DELIBERATE logout from involuntary unauth. The ref is
  // the synchronous source of truth (the session-death detector reads it inside a
  // failure callback); the state is its reactive mirror for the login guard.
  const [signingOut, setSigningOut] = useState(false)
  const signingOutRef = useRef(false)
  /** E7 PR 0b — the ONE way the signed-in user changes (sign-in, another account, a session that died, sign-out): the
   *  session ends FIRST, synchronously, before the UI learns the new user — never on the first sign-in. */
  const setSessionUser = useCallback((next: AuthUser | null, reason: 'session-died' | 'signed-out') => {
    const prev = userNow.current
    const accountEnded = !!prev && prev.id !== next?.id
    if (accountEnded && prev.id !== endedForUserId.current) endSession(next ? 'user-changed' : reason)
    // Accepted (#1316): a stale refresh probe answering "still A" during an awaited signOut clears this mark, so a later end can fire a second time.
    endedForUserId.current = null
    // the same user answering again keeps the object it had, so a re-check re-renders nothing
    if (prev && next && sameUser(prev, next)) return
    userNow.current = next
    sessionUserRef.current = next?.id ?? null
    setUser(next)
    // #1316 (owner): an involuntary account end — another account, a session that died, signed out in another tab —
    // reloads the page now that the new / missing user is known. A deliberate sign-out reloads from `signOut` (after
    // the server call); one in flight here (a probe answering mid-sign-out) leaves it to `signOut`.
    if (accountEnded && reason !== 'signed-out' && !signingOutRef.current) {
      const target = accountEndReloadTarget()
      if (target !== null) reloadAfterAccountEnd(target)
    }
  }, [])
  // backstop: a user change that reached the state some other way still ends the session (after the render)
  useEffect(() => {
    const id = user?.id ?? null
    if (sessionUserRef.current !== null && sessionUserRef.current !== id) endSession('user-changed')
    sessionUserRef.current = id
  }, [user?.id])
  const [isLoading, setIsLoading] = useState(true)

  const refreshSession = useCallback(async (): Promise<AuthUser | null> => {
    // I113 — only an ANSWER means "signed out": get-session answering 200 with no session (or a
    // 401). A request that fails (the API restarting, a 5xx, the network) says nothing about the
    // session: retry with backoff, and if it never answers keep what we had — a restart must not
    // bounce every open tab to /login.
    let session: SessionProbe | null = null
    for (let attempt = 0; ; attempt++) {
      session = await probeSession()
      if (!isTransientProbe(session)) break
      if (attempt >= SESSION_RETRY_DELAYS_MS.length) {
        // the user stays (#1316): a named end before this is spent — a later change of user is a new end
        endedForUserId.current = null
        return userNow.current
      }
      await new Promise((r) => setTimeout(r, SESSION_RETRY_DELAYS_MS[attempt]))
    }
    if (session?.data?.user) {
      const u = session.data.user
      const personalTenantId = u.personalTenantId || u.tenantId || ''
      if (!personalTenantId) {
        console.error('[Auth] User has no personalTenantId — tenant resolution will fail. Check Better Auth additionalFields config.')
      }
      const authUser: AuthUser = {
        id: u.id,
        email: u.email,
        name: u.name,
        personalTenantId,
        emailVerified: u.emailVerified ?? true,
        platformRole: u.platformRole ?? 'user',
      }
      // A session is (re-)established: any prior deliberate-logout state is
      // stale now — clear it so a later involuntary unauth carries ?next= again.
      signingOutRef.current = false
      setSigningOut(false)
      // #1316: this tab's session is an account it did not hold — a sign-in (the login page and the share view's prompt
      // run this after signing in; an OAuth sign-in on the page load it returns to). A sign-in replaces the cookie
      // without signing the previous account out, so the other tabs are told: one still showing another account
      // re-checks and reloads.
      if (userNow.current?.id !== authUser.id) announceSignedIn()
      setSessionUser(authUser, 'session-died')
      return authUser
    }
    setSessionUser(null, 'session-died')
    return null
  }, [setSessionUser])

  // #501 — a session that dies mid-use produces code-less 401s on every
  // authenticated call, but nothing re-checks auth after boot. Watch the one
  // chokepoint every request() failure passes through; on a discriminated
  // session-death 401 (see isSessionDeath401) confirm once via getSession().
  // A dead session returns { data: null } (HTTP 200), so refreshSession() sets
  // user = null, which flips isAuthenticated and fires the login guard.
  const userRef = useRef(user)
  userRef.current = user
  const confirmingRef = useRef(false)
  /** #1316 — when the last tenant-denied re-check ran: a burst of refusals (or a tenant this account really can't open)
   *  asks the server once, not on every refused call. */
  const deniedCheckedAt = useRef(0)
  useEffect(() => {
    return onApiFailure((failure) => {
      // A deliberate logout clears the cookie server-side, so in-flight/background
      // calls 401 during the sign-out window. Do NOT treat those as a session
      // death — the user chose to leave, and confirming here would flip user to
      // null with ?next= before signOut's own clean navigation.
      if (signingOutRef.current) return
      // This browser is changing its password (this tab or another): a 401 now is
      // its OLD cookie being refused before the response installs the new one, not
      // a dead session (#601). A session that really is dead is still caught — the
      // change itself answers 401, and so does the next request after it settles.
      if (sessionRotationInFlight()) return
      // Only act when we BELIEVE we're logged in — this also prevents firing on
      // /login and prevents any redirect loop once user is already null.
      if (!userRef.current) return
      if (confirmingRef.current) return
      // #1316: another tab signed a DIFFERENT account in — its cookie is valid, so this tab's calls are refused 403
      // `tenant_access_denied`, not 401. Re-check who is signed in (once per window): another user → the account end's
      // reload (setSessionUser); the same user → nothing changes (the access-denied screen stands).
      if (isTenantDenied403(failure)) {
        const now = Date.now()
        if (now - deniedCheckedAt.current < TENANT_DENIED_RECHECK_MS) return
        deniedCheckedAt.current = now
      } else if (!isSessionDeath401(failure, { shareActive: getShareSessionToken() != null })) return
      confirmingRef.current = true
      void refreshSession().finally(() => { confirmingRef.current = false })
    })
  }, [refreshSession])

  useEffect(() => {
    refreshSession().finally(() => setIsLoading(false))
  }, [refreshSession])

  // #961: another tab of this browser signed the account out (the drive, or a share
  // view's Log out). Re-check now: the server refuses the ended session, so this tab
  // shows the sign-in page instead of looking signed in until its next failing call.
  useEffect(() => onSignedOutElsewhere(() => {
    if (!userRef.current || signingOutRef.current) return
    // the session-ended helper FIRST (E7 PR 0b): this account is signed out in another tab
    endSession('signed-out-elsewhere')
    endedForUserId.current = userRef.current.id
    void refreshSession()
  }), [refreshSession])

  // #1316: who is signed in may have changed under this tab — another tab signed an account in (its cookie replaced
  // this tab's without a sign-out), or this tab missed that message (it was hidden, frozen, or restored from the
  // back-forward cache). Re-check: another user now → setSessionUser ends this account's session and reloads; the same
  // user → nothing. During a password change the old cookie is refused until the change lands, so the re-check waits
  // for it instead of being dropped (the rotation flag expires on its own).
  const rotationRecheck = useRef<ReturnType<typeof setTimeout> | null>(null)
  const recheckAccount = useRef<() => void>(() => {})
  recheckAccount.current = () => {
    if (!userRef.current || signingOutRef.current) return
    if (sessionRotationInFlight()) {
      rotationRecheck.current ??= setTimeout(() => { rotationRecheck.current = null; recheckAccount.current() }, ROTATION_RECHECK_MS)
      return
    }
    void refreshSession()
  }
  useEffect(() => () => { if (rotationRecheck.current) clearTimeout(rotationRecheck.current) }, [])
  useEffect(() => onSignedInElsewhere(() => recheckAccount.current()), [])
  const returnCheckedAt = useRef(0)
  useEffect(() => {
    const onReturn = (): void => {
      if (document.visibilityState !== 'visible') return
      const now = Date.now()
      if (now - returnCheckedAt.current < RETURN_RECHECK_MS) return
      returnCheckedAt.current = now
      recheckAccount.current()
    }
    const onPageShow = (e: PageTransitionEvent): void => {
      if (!e.persisted) return
      returnCheckedAt.current = Date.now()
      recheckAccount.current()
    }
    document.addEventListener('visibilitychange', onReturn)
    window.addEventListener('focus', onReturn)
    window.addEventListener('pageshow', onPageShow)
    return () => {
      document.removeEventListener('visibilitychange', onReturn)
      window.removeEventListener('focus', onReturn)
      window.removeEventListener('pageshow', onPageShow)
    }
  }, [])

  const signOut = useCallback(async () => {
    // Mark the logout as deliberate BEFORE clearing the cookie, so the detector
    // above ignores the 401s that clearing it produces and the guard redirects
    // to a clean /login (no ?next=). Set synchronously (ref) and reactively
    // (state) so both the callback and the render path see it immediately.
    signingOutRef.current = true
    setSigningOut(true)
    // E7 PR 0b: the session-ended helper FIRST — the session's in-flight work (a Bin / undo restore) is dropped
    endSession('signed-out')
    endedForUserId.current = userNow.current?.id ?? null
    // better-auth answers a refused sign-out with `{ error }` rather than throwing; either way the cookie may still be
    // valid, so the page is not reloaded (a load would sign the person straight back in) — the UI leaves locally.
    let signedOutOnServer = false
    try {
      const res = await authClient.signOut() as { error?: unknown } | null | undefined
      signedOutOnServer = !res?.error
    } finally {
      // Local cleanup runs even if the server call rejected. The user asked to
      // leave, so honor it locally — and, critically, never leave `user`
      // non-null with `signingOut` stuck true, which would suppress the
      // session-death detector for a later real expiry (#501 fresh-review).
      // Clear user-scoped localStorage (clipboard, workspace state, agent history).
      // Keep: papan-language (device preference), papan-display-options (UI pref).
      const keysToRemove = ['papan-clipboard', 'papan-open-workspaces', 'papan-default-persona']
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i)
        if (!key) continue
        if (keysToRemove.includes(key)) localStorage.removeItem(key)
        // Clear workspace-scoped keys (agent tabs, prompt history, frecency)
        if (key.startsWith('papan-agent-tabs:') || key.startsWith('papan-prompt-history:') || key.startsWith('papan-frecency:')) {
          localStorage.removeItem(key)
        }
      }
      setSessionUser(null, 'signed-out')
      announceSignedOut()
    }
    // #1316 (owner): the account end's full page load — after the server cleared the cookie and the other tabs were
    // told. A clean /login (#501: a deliberate logout carries no ?next=).
    if (signedOutOnServer) reloadAfterAccountEnd('/login')
  }, [setSessionUser])

  const value: AuthContextValue = {
    user,
    personalTenantId: user?.personalTenantId ?? null,
    isLoading,
    isAuthenticated: !!user,
    signingOut,
    signOut,
    refreshSession,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
